import { useEffect, useMemo, useState } from 'react'

import { Control, Controller, ControllerRenderProps } from 'react-hook-form'

import { Box, Flex, Spacer } from '@chakra-ui/react'

import { hideScrollbarCss } from '@/styles/utils'
import { OptionI } from '@/utils/forms/optionBuilders'

import { Button } from '../Button'
import { CountBadgeAccordion } from '../CountBadge/components/CountBadgeAccordion'
import { SearchInput } from '../SearchInput'
import { MultiSelectCheckbox } from './MultiSelectCheckbox'
import { MultiSelectListSkeleton } from './MultiSelectListSkeleton'
import {
  createIsSelectedChecker,
  filterOptions,
  getSortedOptions,
  hasUnselectedOptions,
  removeDuplicateOptions,
} from './utils'

export interface MultiSelectCommonIProps {
  placeholder?: string
  options?: OptionI[]
  defaultValue?: OptionI[]
  control: Control
  id: string
  isExpanded?: boolean
  onSearchChange?: (searchTerm: string) => void
  searchValue?: string
  isLoading?: boolean
  shouldSortOptions?: boolean
}

interface MultiSelectIProps extends MultiSelectCommonIProps {
  numberSelected: number
  onNumberSelectedChange: (n: number) => void
}

export const MultiSelect = ({
  placeholder = 'Search...',
  options,
  defaultValue,
  control,
  id,
  isExpanded,
  onSearchChange,
  searchValue,
  isLoading = false,
  numberSelected,
  onNumberSelectedChange,
  shouldSortOptions = true,
}: MultiSelectIProps) => {
  const [internalSearch, setInternalSearch] = useState('')
  const [availableOptions, setAvailableOptions] = useState<OptionI<string>[]>(
    []
  )

  // Use external search if provided, otherwise use internal search
  const isExternalSearch = !!onSearchChange
  const currentSearchValue = isExternalSearch
    ? searchValue ?? ''
    : internalSearch

  const onToggle = (
    option: OptionI,
    selectedOptions: OptionI[],
    newIsChecked: boolean,
    onChange: (value: OptionI[]) => void
  ) => {
    const newValue = newIsChecked
      ? // Add option
        [...(selectedOptions ?? []), option]
      : // Remove option
        selectedOptions.filter((o) => o.value !== option.value)
    onChange(newValue)
  }

  const onClear = (onChange: (value: OptionI[]) => void) => {
    // Clear all selected options
    onChange([])
  }

  const onSelectAll = (
    availableOptions: OptionI[],
    selectedOptions: OptionI[],
    onChange: (value: OptionI[]) => void
  ) => {
    // Select all available options preserving already selected options
    onChange(removeDuplicateOptions([...availableOptions, ...selectedOptions]))
  }

  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={id}
      render={({ value: formValue, onChange }: ControllerRenderProps) => {
        const selectedOptions = formValue ?? []
        const allOptions = useMemo(
          () =>
            // When shouldSortOptions is false, preserve the original options order
            // by putting options first (removeDuplicateOptions keeps first occurrence)
            shouldSortOptions
              ? removeDuplicateOptions([...selectedOptions, ...(options ?? [])])
              : removeDuplicateOptions([
                  ...(options ?? []),
                  ...selectedOptions,
                ]),
          [selectedOptions, options, shouldSortOptions]
        )

        // Filter options based on the search term
        const filteredOptions = filterOptions(
          allOptions,
          currentSearchValue,
          isExternalSearch
        )

        // Determine button states
        const isClearDisabled = selectedOptions?.length === 0
        const isSelectAllDisabled = !hasUnselectedOptions(
          filteredOptions,
          selectedOptions
        )

        // Update Selected option count
        onNumberSelectedChange(selectedOptions?.length ?? 0)

        // Update availableOptions when options have changed
        useEffect(() => {
          if (filteredOptions.length !== availableOptions.length) {
            setAvailableOptions(filteredOptions)
          }
        }, [filteredOptions])

        // Re-orders selected options to the top
        useEffect(() => {
          if (isExpanded && shouldSortOptions) {
            setAvailableOptions(
              getSortedOptions(filteredOptions, selectedOptions)
            )
          }
        }, [isExpanded])

        return (
          <Box>
            <SearchInput
              mb='15px'
              onChange={(e) => {
                isExternalSearch
                  ? onSearchChange?.(e.target.value)
                  : setInternalSearch(e.target.value)
              }}
              placeholder={placeholder}
              showIcon={false}
              value={currentSearchValue}
            />
            <Flex alignItems='center' dir='row' mb='15px'>
              <Button
                _active={{ color: 'brand.200' }}
                _focus={{ outline: 'none' }}
                _hover={{ textDecoration: 'none' }}
                color='brand.200'
                isDisabled={isSelectAllDisabled}
                onClick={() =>
                  onSelectAll(availableOptions, selectedOptions, onChange)
                }
                textDecoration='none'
                variant='link'
              >
                Select All
              </Button>
              <Box backgroundColor='black' height='20px' mx='12px' w='1px' />
              <Button
                _focus={{ outline: 'none' }}
                _hover={{ textDecoration: 'none' }}
                color='black'
                isDisabled={isClearDisabled}
                onClick={() => {
                  onClear(onChange)
                }}
                textDecoration='none'
                variant='link'
              >
                Clear
              </Button>
              <Spacer />
              <CountBadgeAccordion text={`${numberSelected} Selected`} />
            </Flex>
            {isLoading ? (
              <MultiSelectListSkeleton />
            ) : (
              <Box
                css={hideScrollbarCss}
                maxH='194px' // 6 items * (24px heigth + 10px padding) - 10px padding (on the last item)
                overflowX='hidden'
                overflowY='scroll'
              >
                {(() => {
                  const isSelected = createIsSelectedChecker(selectedOptions)
                  return availableOptions.map((option) => {
                    const isChecked = isSelected(option)
                    return (
                      <MultiSelectCheckbox
                        isChecked={isChecked}
                        key={option.value}
                        onChange={(e) => {
                          onToggle(
                            option,
                            selectedOptions,
                            e.target.checked,
                            onChange
                          )
                        }}
                        option={option}
                      />
                    )
                  })
                })()}
              </Box>
            )}
          </Box>
        )
      }}
    />
  )
}
